import React, { useEffect, useState } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { api } from '../../../infrastructure/api'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import guid, { Guid } from '../../../infrastructure/guid'
import { t } from '../../../infrastructure/i18nextHelper'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { ColumnDescriptor, Select, TableItem, TextField } from '../../common/customComponents'
import { Item } from '../../common/components/select/types'
import { createExcelLines, MasterDataItem, MasterDataShell } from './masterDataShell'

type TranscoType = 'Counterparty' | 'Product' | 'Site'

type PipeLineTransco = {
    id: Guid
    pipeNetwork: string
    externalValue: string
    direction: string
    transcoValue: string
}

const translationBase = 'admin.masterdata.publicPipeLineTransco.'
const apiBase = 'masterdata/publicPipeLineTransco/'
const fields: (keyof Omit<PipeLineTransco, 'id'>)[] = ['pipeNetwork', 'externalValue', 'direction', 'transcoValue']

function PublicPipeLineTranscoMasterData({ classes, type }: MuiProps & { type: TranscoType }) {
    const excelGenerator = ExcelGeneratorContainer.useContainer()
    const isManager = hasClaim(Claims.MasterdataPublicPipeLineTranscoManager)
    const url = apiBase + type
    const [transcos, setTranscos] = useState<PipeLineTransco[]>([])
    const [choices, setChoices] = useState<Item[]>([])
    const [filters, setFilters] = useState<Record<keyof Omit<PipeLineTransco, 'id'>, string | null>>({
        pipeNetwork: '',
        externalValue: '',
        direction: '',
        transcoValue: ''
    })

    const load = async () => {
        const choices: Item[] = type === 'Site'
            ? await api.get<{ code: string; name: string }[]>(`${apiBase}sites`).then(sites =>
                sites.map(site => ({ value: site.code, text: site.name })))
            : type === 'Product'
                ? await api.get<{ productId: Guid; product: string; name: string }[]>(`${apiBase}products`).then(products =>
                    products.map(product => ({
                        value: product.product,
                        text: product.name
                    })))
                : []
        const values = await api.get<PipeLineTransco[]>(url)
        setTranscos(values)
        setChoices(choices)
    }

    useEffect(() => {
        load()
    }, [])

    const items = transcos
        .filter(item => fields.every(field =>
            String(item[field] ?? '').toLowerCase().includes((filters[field] ?? '').toLowerCase())))
        .map(item => ({ ...item, isModified: false }))

    const columns: ColumnDescriptor<TableItem<MasterDataItem<PipeLineTransco>>>[] = fields.map(field => ({
        name: t(translationBase + (field === 'transcoValue' ? type : field)),
        value: item => item[field],
        columnFilter: {
            value: filters[field],
            onChange: value => setFilters(previous => ({ ...previous, [field]: value ?? null }))
        }
    }))

    const onSave = async (item: PipeLineTransco) => {
        await api.post(url, {
            id: item.id === guid.empty ? null : item.id,
            pipeNetwork: item.pipeNetwork,
            externalValue: item.externalValue,
            direction: item.direction,
            transcoValue: item.transcoValue
        })
        await load()
        return true
    }

    const onDelete = async (ids: string[] | null) => {
        await Promise.all((ids ?? []).map(id => api.del(`${apiBase}${id}`)))
        await load()
        return true
    }

    const onImportExcel = async (file: Blob) => {
        await api.upload(`${url}/import`, file, 'file', { withReport: 'dialog' })
        await load()
    }

    const onExportExcel = () => {
        excelGenerator.generate({
            filename: `Transcos${type}.xlsx`,
            sheets: [{ name: type, lines: createExcelLines(items, columns) }]
        })
    }

    const emptyTransco = (): PipeLineTransco => ({
        id: guid.empty,
        pipeNetwork: '',
        externalValue: '',
        direction: '',
        transcoValue: '',
    })

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId={`public-pipeline-transco-${type}`}
                headerLabel={t(translationBase + `${type}Transcos`)}
                itemLabel={t(translationBase + type)}
                isManager={isManager}
                items={items}
                columns={columns}
                onExportExcel={onExportExcel}
                onImportExcel={onImportExcel}
                onNew={emptyTransco}
                onDelete={onDelete}
                onSave={onSave}>
                {(selectedItem, setSelectedItem) => (
                    <>
                        <TextField label={t(translationBase + 'pipeNetwork')}
                            text={selectedItem.pipeNetwork}
                            disabled={selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, pipeNetwork: event.target.value })} />
                        <TextField label={t(translationBase + 'externalValue')}
                            text={selectedItem.externalValue}
                            disabled={selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, externalValue: event.target.value })} />
                        <Select label={t(translationBase + 'direction')}
                            value={selectedItem.direction}
                            disabled={selectedItem.id !== guid.empty}
                            choices={[
                                { value: '', text: t(translationBase + 'noDirection') },
                                { value: 'O', text: 'O' },
                                { value: 'D', text: 'D' }
                            ]}
                            onChange={direction => setSelectedItem({ ...selectedItem, direction })} />
                        {type === 'Counterparty'
                            ? <TextField label={t(translationBase + type)}
                                text={selectedItem.transcoValue}
                                onChange={event => setSelectedItem({ ...selectedItem, transcoValue: event.target.value })} />
                            : <Select label={t(translationBase + type)}
                                value={selectedItem.transcoValue}
                                choices={choices}
                                onChange={value => setSelectedItem({
                                    ...selectedItem,
                                    transcoValue: value
                                })} />
                        }
                    </>
                )}
            </MasterDataShell>
        </div>
    )
}

const styles = () => createStyles({ container: { height: '100%' } })

export const TranscosCounterPartyMasterData = withStyles(styles, muiOptions)((props: MuiProps) =>
    <PublicPipeLineTranscoMasterData {...props} type='Counterparty' />)

export const TranscosProductMasterData = withStyles(styles, muiOptions)((props: MuiProps) =>
    <PublicPipeLineTranscoMasterData {...props} type='Product' />)

export const TranscosSiteMasterData = withStyles(styles, muiOptions)((props: MuiProps) =>
    <PublicPipeLineTranscoMasterData {...props} type='Site' />)
